package com.hyperether.planner.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.itemsIndexed
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import com.hyperether.planner.model.Event
import com.hyperether.planner.model.Plan
import com.hyperether.planner.util.Dp
import com.hyperether.planner.util.Sp
import com.hyperether.planner.util.verticalScrollbarGrid
import kotlinx.datetime.LocalDate
import kotlinx.datetime.format.DayOfWeekNames
import kotlinx.datetime.isoDayNumber

@Composable
fun ScheduleMonthPlannerWeb(
    listOfPlans: MutableList<Plan>,
    month: Int,
    year: Int,
    selectedDay: State<Int?>,
    language: String = "en",
    borderPathColor: Color = Color(231, 231, 231),
    onEventClicked: (Event) -> Unit,
    onEventHovered: (Event?) -> Unit
) {
    val list = mutableStateListOf<CalendarField>()
    val numberOfCells = 35
    val eventMap = groupEventsByDay(listOfPlans)

    val currentMonthDays = monthDays(year, month)
    val firstDayOfWeek = LocalDate(year, month, 1).dayOfWeek
    val prevMonth: Int
    val prevYear: Int
    if (month == 1) {
        prevMonth = 12
        prevYear = year - 1
    } else {
        prevMonth = month - 1
        prevYear = year
    }
    val previousMonthDays = monthDays(prevYear, prevMonth)

    val startOffset = (firstDayOfWeek.isoDayNumber - 1) % 7

    for (i in 0 until startOffset) {
        list.add(CalendarField(previousMonthDays - startOffset + i + 1, false))
    }
    for (i in 1..currentMonthDays) {
        list.add(CalendarField(i, true))
    }
    val remainingCells = numberOfCells - list.size
    for (i in 1..remainingCells) {
        list.add(CalendarField(i, false))
    }

    // Define German abbreviated day names
    val germanDayNames = listOf("MO", "DI", "MI", "DO", "FR", "SA", "SO")
    val dayNames = if (language == "de") {
        germanDayNames
    } else {
        DayOfWeekNames.ENGLISH_ABBREVIATED.names.map { it.uppercase() }
    }

    Column {
        Box(modifier = Modifier.fillMaxWidth().height(Dp(1f)).background(borderPathColor))
        Row(
            modifier = Modifier.fillMaxWidth().background(Color(0xFFF6F7F9))
                .padding(vertical = Dp(10f))
        ) {
            dayNames.forEach { dayName ->
                Text(
                    dayName,
                    textAlign = TextAlign.Start,
                    modifier = Modifier.weight(1f).padding(start = Dp(10f)),
                    color = Color(0xFF6F7C8E),
                    fontWeight = FontWeight.Bold,
                    maxLines = 1,
                    fontSize = Sp(14f),
                    overflow = TextOverflow.Ellipsis
                )
            }
        }

        val gridState = rememberLazyGridState()
        LazyVerticalGrid(
            columns = GridCells.Fixed(7),
            state = gridState,
            modifier = Modifier
                .fillMaxWidth()
                .verticalScrollbarGrid(
                    state = gridState,
                    columns = 7
                ),
        ) {
            if (list.isNotEmpty()) {
                itemsIndexed(list.takeIf { it.isNotEmpty() } ?: emptyList()) { index, day ->
                    Box(
                        modifier = Modifier.fillMaxWidth().aspectRatio(1.3f)
                            .border(Dp(1f), color = borderPathColor)
                    ) {
                        Column {
                            Box(
                                modifier = Modifier.size(Dp(40f)),
                                contentAlignment = Alignment.Center
                            ) {
                                val selected =
                                    selectedDay.value == day.day && index > startOffset - 1 && index < list.size - remainingCells
                                Box(
                                    modifier = Modifier
                                        .size(Dp(30f)),
                                    contentAlignment = Alignment.Center // Centers the content within the Box
                                ) {
                                    Text(
                                        text = "${day.day}",
                                        color = Color.Black,
                                        textAlign = TextAlign.Center,
                                        maxLines = 1,
                                        fontSize = Sp(14f),
                                        overflow = TextOverflow.Ellipsis
                                    )
                                }
                            }
                            if (day.fromCurrentMonth) {
                                val eventList: List<Event>? = eventMap[day.day]
                                if (eventList?.isNotEmpty() == true) {
                                    Column(
                                        modifier = Modifier.padding(Dp(10f)).verticalScroll(
                                            rememberScrollState()
                                        )
                                    ) {
                                        eventList.forEach { event ->
                                            val modifier =
                                                Modifier.clickable {
                                                    onEventClicked(event)
                                                }
                                                    .pointerHoverIcon(PointerIcon.Hand)
                                                    .pointerInput(Unit) {
                                                        awaitPointerEventScope {
                                                            while (true) {
                                                                val pe = awaitPointerEvent()
                                                                val pointer =
                                                                    pe.changes.firstOrNull()
                                                                pointer?.let {
                                                                    if (it.position.x > 5 && it.position.x < (size.width - 5) &&
                                                                        it.position.y > 5 && it.position.y < (size.height - 5)
                                                                    ) {
                                                                        onEventHovered(event)
                                                                    } else {
                                                                        onEventHovered(null)
                                                                    }
                                                                }

                                                            }
                                                        }
                                                    }

                                            Box(
                                                modifier = modifier.fillMaxWidth()
                                                    .height(Dp(40f))
                                                    .clip(RoundedCornerShape(Dp(8f)))
                                                    .background(event.color)
                                                    .padding(Dp(4f)),
                                                contentAlignment = Alignment.Center
                                            ) {
                                                Text(
                                                    event.title ?: "",
                                                    maxLines = 1,
                                                    fontSize = Sp(14f),
                                                    overflow = TextOverflow.Ellipsis
                                                )
                                            }
                                            Spacer(Modifier.height(Dp(4f)))
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
